import classnames from "classnames";
import {
  AnalyticsComponent,
  GroupCardModal,
  ListMap,
  WarningModal,
} from "components";
import { MapIcon, ViewListIcon } from "@heroicons/react/outline";
import { memo, useCallback, useMemo, useState } from "react";
import { ReactComponent as SquareLeft } from "assets/images/square-left.svg";
import { ReactComponent as SquareRight } from "assets/images/square-right.svg";

import { useOutletContext } from "react-router-dom";
import { useResizeDetector } from "react-resize-detector";
import { IOutletAuth } from "types";

import { GroupCard } from "./GroupCard";

import { useGroupListFilterParams } from "./GroupList.hooks";
import { useWindowDimensions } from "hooks";
import JumboImage1 from "assets/images/jumbo-1.png";

import { getFirstLocationInActionGroupEntity } from "utils";

function GroupList() {
  const authContext = useOutletContext<IOutletAuth>();

  const [open, setOpen] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [openedCardId, setOpenedCardId] = useState<string | undefined>(
    undefined
  );
  const [openCardLocations, setOpenCardLocations] = useState<
    (string | null | undefined)[] | undefined
  >([]);

  const { width, height, ref: mapRef } = useResizeDetector();

  const [isIssueHovered, setIsIssueHovered] = useState(false);

  const [hoveredIssueId, setHoveredIssueId] = useState<
    string | undefined | string[]
  >(undefined);
  const [hoveredIssueType, setHoveredIssueType] = useState<
    "regular" | "city" | "country" | undefined
  >();

  const dim = useWindowDimensions();

  const disabled = useMemo(() => {
    return !!(open && dim.width && dim.width <= 1023);
  }, [dim.width, open]);

  const [issueHoveredProps, setIssueHoveredProps] = useState({
    lat: NaN,
    lng: NaN,
  });

  const { data, inViewRef } = useGroupListFilterParams(
    width || dim.width,
    disabled
  );

  const [openWarningModal, setOpenWarningModal] = useState(false);
  const handleCreateGroup = () => {
    setOpenWarningModal(true);
  };

  return (
    <div className={classnames("flex flex-col relative h-full pb-32 md:pb-20 lg:pb-12")}>
      <div
        className={classnames(
          "grid lg:grid-cols-[1fr_1fr_1fr_1.3fr]   h-full lg:h-[calc(100vh-118px)]  lg:mr-0 gap-4    "
        )}
      >
        <div
          className={classnames(
            "eight-fourth-step nine-fourth-step grid grid-cols-1 auto-rows-max gap-4 lg:overflow-scroll pb-4 pt-[3px] pl-[3px]    pr-2 max-w-[2000px] transition-all duration-500 ease-linear  ",
            {
              "sm:grid-cols-2 lg:grid-cols-3 lg:col-span-3": !open,
              "hidden lg:grid max-w-[500px]": open,
            }
          )}
        >
          <div
            className={classnames(
              " relative    flex-1 max-h-[300px]  w-full  overflow-hidden",
              {
                " flex col-span-1 sm:col-span-2  lg:col-span-3": !open,
              },
              {
                hidden: open,
              }
            )}
          >
            <div className=" flex justify-center flex-1 w-full h-full">
              <img
                style={{ flexBasis: "100%" }}
                className="select-none pointer-events-none min-h-[120px] sm:min-h-[180px] flex-1 w-full h-full object-contain"
                alt="jumbo"
                src={authContext.backgroundImages || JumboImage1}
              />
            </div>
          </div>
          {data?.pages.map((page, pagesIndex) => {
            return page.data.items.length < 1 ? (
              <div
                className="flex   justify-center col-span-6"
                key={pagesIndex}
              >
                <p className=" max-w-[700px] text-center text-xl">
                  There are no action groups found for the chosen location,
                  please adjust your search parameters or
                  <span
                    onClick={handleCreateGroup}
                    className="font-bold px-1 hover:text-white cursor-pointer"
                  >
                    create a group
                  </span>
                </p>
              </div>
            ) : (
              //TODO - CHANGE ON BACK
              page.data.items.map((actionGroup, issueIndex) => {
                const firstLocation =
                  getFirstLocationInActionGroupEntity(actionGroup);

                const geoLocation = {
                  lat: firstLocation?.lat,
                  lng: firstLocation?.lng,
                };
                const isLast =
                  data?.pages.length === pagesIndex + 1 &&
                  page.data.items.length === issueIndex + 1;

                const groupId = actionGroup.id!;

                const displayLabel = firstLocation?.displayLabel;
                const isFormattedAddress = !!(
                  displayLabel || firstLocation?.formattedAddress
                );
                const isCountry = !!firstLocation?.country;
                const isCity = !!firstLocation?.city;
                const isStreetNumber = !!firstLocation?.streetNumber;
                const isRoute = !!firstLocation?.route;
                const location =
                  typeof firstLocation === "object"
                    ? isFormattedAddress
                      ? `${displayLabel || firstLocation?.formattedAddress}`
                      : isCountry && isCity && isStreetNumber && isRoute
                      ? `${firstLocation.route} ${firstLocation?.streetNumber}, ${firstLocation?.city}, ${firstLocation?.country}`
                      : isCountry && isCity
                      ? ` ${firstLocation?.city}, ${firstLocation?.country}`
                      : isCountry
                      ? `${firstLocation?.country}`
                      : "No location"
                    : "No location";

                const allLocations = actionGroup?.solutions?.map((solution) => {
                  return solution?.issue?.location?.formattedAddress;
                });

                const rate = actionGroup?.rates && actionGroup?.rates[0];

                return (
                  <div
                    className={classnames(
                      "relative  maxmd:flex cursor-pointer",
                      {
                        " first-card-to-click ": issueIndex === 0,
                      }
                    )}
                    key={actionGroup?.id}
                    onClick={() => {
                      setOpenedCardId(actionGroup?.id);
                      setOpenCardModal(true);
                      setOpenCardLocations(allLocations);
                    }}
                  >
                    {actionGroup.analyticsData && (
                      <AnalyticsComponent
                        analyticsData={actionGroup.analyticsData}
                      />
                    )}
                    <GroupCard
                      setOpenCardModal={setOpenCardModal}
                      groupId={groupId}
                      forceHoverIssue={hoveredIssueId?.includes(
                        actionGroup.id || ""
                      )}
                      membersNumber={String(actionGroup.membersCount)}
                      skills={actionGroup.skills}
                      setIsIssueHovered={setIsIssueHovered}
                      setIssueHoveredProps={setIssueHoveredProps}
                      setHoveredIssueType={setHoveredIssueType}
                      geoLocation={geoLocation}
                      innerRef={isLast ? inViewRef : undefined}
                      title={actionGroup?.title || ""}
                      profileImage={actionGroup?.user?.avatar}
                      groupImage={actionGroup.logo}
                      createdAt={actionGroup?.createdAt}
                      likes={actionGroup?.likes || 0}
                      dislikes={actionGroup?.dislikes || 0}
                      location={location}
                      tags={actionGroup?.tags}
                      allLocations={allLocations}
                      isSoftDeleted={!!actionGroup?.deletedAt}
                      rate={rate}
                    />
                  </div>
                );
              })
            );
          })}
        </div>
        <div
          className={classnames("relative lg:rounded-lg lg:overflow-hidden ", {
            "hidden lg:flex lg:flex-col row-start-1 lg:col-start-4": !open,
            "lg:col-start-2 lg:col-end-[-1] ": open,
          })}
        >
          <ListMap
            setOpenMap={setOpen}
            hoveredIssueType={hoveredIssueType}
            setHoveredIssueId={setHoveredIssueId}
            issueCardHovered={isIssueHovered}
            issueCardProps={issueHoveredProps}
            innerRef={mapRef}
            mapHeight={height || dim.height}
            mapWidth={width || dim.width}
          />

          <div
            className="flex flex-col absolute top-7 left-5 cursor-pointer"
            onClick={useCallback(() => setOpen((open) => !open), [])}
          >
            {!open && <SquareLeft />}
            {open && <SquareRight />}
          </div>
        </div>
      </div>
      {/* Floating Map/List Toggle - Mobile Only */}
      <button
        onClick={() => setOpen((open) => !open)}
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 py-2.5 rounded-full shadow-lg transition-all duration-300 ease-out lg:hidden active:scale-95"
        style={{
          background: 'rgba(250, 247, 242, 0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(194, 65, 12, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(194, 65, 12, 0.08)',
        }}
      >
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-200"
          style={{
            background: 'rgba(194, 65, 12, 0.1)',
          }}
        >
          {open ? (
            <ViewListIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          ) : (
            <MapIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          )}
        </span>
        <span
          className="text-sm font-medium"
          style={{
            fontFamily: "'Source Sans 3', sans-serif",
            color: '#44403C',
          }}
        >
          {open ? "View Groups" : "View Map"}
        </span>
      </button>
      <div className="md:hidden h-24" aria-hidden />
      {openCardModal && openedCardId && (
        <GroupCardModal
          id={openedCardId}
          setShow={setOpenCardModal}
          mapWidth={width || dim.width}
          allLocations={openCardLocations}
        />
      )}
      {openWarningModal && <WarningModal setShow={setOpenWarningModal} />}
    </div>
  );
}

export default memo(GroupList);
